import {ListType, QueryListsResponseType} from "../../definitions/types";
import {ListModel} from "../domain/listModel";

export class ListsModel {
    private readonly _matches: number[];
    private readonly _lists: ListModel[];

    constructor(readonly response?: QueryListsResponseType) {
        this._matches = response?.matchingListIds ?? [] as number[];
        this._lists = response?.lists
            .filter((list): list is ListType => list !== null)
            .map(list => new ListModel(list)) ?? [] as ListModel[];
    }

    /**
     * @property matches
     * @description The list of list ids that match the query.
     * @returns {string[]}
     */
    public get matches(): number[] {
        return this._matches;
    }

    /**
     * @method matchCount
     * @description Returns the number of lists that match the query.
     * @returns {number}
     */
    public get matchCount(): number {
        return this._matches.length;
    }

    /**
     * @method hasOnlyOneMatch
     * @description Returns true if there is only one list that matches the query.
     * @returns {boolean}
     */
    public get hasOnlyOneMatch(): boolean {
        return this.matchCount === 1;
    }

    /**
     * @method hasMatches
     * @description Returns true if there are lists that match the query.
     * @returns {boolean}
     */
    public get hasMatches(): boolean {
        return this.matchCount > 0;
    }

    /**
     * @property lists
     * @description The list of lists that match the query.
     * @returns {ListModel[]}
     */
    public get lists(): ListModel[] {
        return this._lists;
    }

    /**
     * @method listCount
     * @description Returns the number of lists that match the query.
     * @returns {number}
     */
    public get listCount(): number {
        return this._lists.length;
    }

    /**
     * @method hasLists
     * @description Returns true if there are lists that match the query.
     * @returns {boolean}
     */
    public get hasLists(): boolean {
        return this.listCount > 0;
    }

    /**
     * @method hasOnlyOneList
     * @description Returns true if there is only one list that matches the query.
     * @returns {boolean}
     */
    public get hasOnlyOneList(): boolean {
        return this.listCount === 1;
    }

    /**
     * @method hasMoreThanOneList
     * @description Returns true if there is more than one list that matches the query.
     * @returns {boolean}
     */
    public get hasMoreThanOneList(): boolean {
        return this.listCount > 1;
    }
}